import i18n from '@/plugins/i18n';
import { User, UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client';
import { appService, authorizationManagerService } from '..';

export default class AuthenticationService {
    private manager?: UserManager;

    public get userManager(): UserManager {
        if (this.manager) {
            return this.manager;
        }

        const app = appService.getCurrentApplication();
        const settings: UserManagerSettings = {
            userStore: new WebStorageStateStore({ store: window.localStorage }),
            stateStore: new WebStorageStateStore({ store: window.localStorage }),
            authority: process.env.VUE_APP_IDENTITY_SERVER_URL,
            client_id: app.clientId,
            client_secret: 'FolkyTools.Frontend.eCommerce.Webs.MissionControl',
            redirect_uri: `${app.clientUri}/authentication-callback`,
            automaticSilentRenew: true,
            silent_redirect_uri: `${app.clientUri}/authentication-silent-renew`,
            response_type: 'id_token token',
            scope: 'openid profile email roles ApiAccess',
            filterProtocolClaims: true,
            ui_locales: i18n.locale,
        };

        this.manager = new UserManager(settings);

        this.manager.events.addAccessTokenExpired(() => {
            // Only reset in-memory permissions. The router guard already handles re-authentication
            // via signinSilent() on the next navigation, avoiding an unnecessary redirect.
            authorizationManagerService.resetPermissions();
        });

        this.manager.events.addSilentRenewError(async () => {
            authorizationManagerService.resetPermissions();
            await this.manager!.removeUser();
            // No redirect here: the router guard will call signinSilent() on the next navigation.
            // If the IS session is still active, the user won't notice. Otherwise, they are redirected to /prelogin.
        });

        return this.manager;
    }

    public get user(): Promise<User | null> {
        return this.userManager.getUser();
    }

    public get getUserId(): Promise<number | undefined> {
        return this.user.then(userData => {
            if (userData && userData.profile) {
                return +userData.profile.sub;
            }
            return undefined;
        });
    }

    public get getUserEmail(): Promise<string | undefined> {
        return this.user.then(userData => {
            if (userData && userData.profile) {
                return userData.profile.email;
            }
            return undefined;
        });
    }

    public get accessToken(): Promise<string> {
        return this.user.then(user => user!.access_token);
    }

    public get isAuthenticated(): Promise<boolean> {
        return this.user.then(user => user !== null && !user.expired);
    }

    public async login(): Promise<void> {
        const app = appService.getCurrentApplication();
        await this.userManager.signinRedirect({
            extraQueryParams: {
                initProcessUrl: window.location.href
            },
            state: window.location.href === `${app.clientUri}/`
                ? '/dashboard'
                : window.location.href.replace(app.clientUri!, ''),
        });
    }

    public async logout(state: string = ''): Promise<void> {
        authorizationManagerService.resetPermissions();

        const user = await this.userManager.getUser();

        if (!user || user.expired) {
            await this.userManager.removeUser();
            window.location.href = '/';
            return;
        }

        const app = appService.getCurrentApplication();

        await this.userManager.signoutRedirect({ post_logout_redirect_uri: `${app.clientUri}${state}` });
    }
}

export const authenticationService = new AuthenticationService();
